import axios from "axios";
import routes from "../assets/apiRoutes.json";
import { EMPTY_GUID } from "../enum/Enum";

/**
 * Client for the report-grouping API (rt/ReportGroup/*). Distinct from the
 * existing report services. All calls take the OIDC access token.
 */

const authHeaders = (accessToken) => ({
  headers: {
    "Content-Type": "application/json",
    access_token: accessToken,
    Authorization: `Bearer ${accessToken}`,
  },
});

const apiUrl = (route) => `${window._env_.REACT_APP_APIS}/${route}`;

/** Grouped report menu for a module (groups + their reports). */
export const getReportMenu = async (module, accessToken) => {
  try {
    const res = await axios.get(
      `${apiUrl(routes.reportGroups.GetReportMenu)}?module=${encodeURIComponent(module ?? "")}`,
      authHeaders(accessToken)
    );
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error("getReportMenu failed", err);
    return [];
  }
};

/**
 * Grouped report menu for ALL modules in a single call. Each returned entry
 * carries its own `Module`, so the caller groups the payload by module on the
 * client instead of hitting the API once per module.
 */
export const getReportMenuAll = async (accessToken) => {
  try {
    const res = await axios.get(apiUrl(routes.reportGroups.GetReportMenu), authHeaders(accessToken));
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error("getReportMenuAll failed", err);
    return [];
  }
};

export const getGroups = async (module, accessToken) => {
  const res = await axios.get(
    `${apiUrl(routes.reportGroups.GetGroups)}?module=${encodeURIComponent(module ?? "")}`,
    authHeaders(accessToken)
  );
  return res.data;
};

export const insertGroup = async (group, accessToken) => {
  const res = await axios.post(apiUrl(routes.reportGroups.InsertGroup), group, authHeaders(accessToken));
  return res.data;
};

export const updateGroup = async (group, accessToken) => {
  const res = await axios.put(apiUrl(routes.reportGroups.UpdateGroup), group, authHeaders(accessToken));
  return res.data;
};

export const deleteGroup = async (rowId, accessToken) => {
  const res = await axios.post(apiUrl(routes.reportGroups.DeleteGroup), JSON.stringify(rowId), authHeaders(accessToken));
  return res.data;
};

export const getGroupItems = async (groupRowId, accessToken) => {
  const res = await axios.get(
    `${apiUrl(routes.reportGroups.GetReportGroupItems)}?groupKey=${encodeURIComponent(groupRowId)}`,
    authHeaders(accessToken)
  );
  return res.data;
};

export const insertItem = async (item, accessToken) => {
  const res = await axios.post(apiUrl(routes.reportGroups.InsertReportGroupItem), item, authHeaders(accessToken));
  return res.data;
};

export const updateItem = async (item, accessToken) => {
  const res = await axios.put(apiUrl(routes.reportGroups.UpdateReportGroupItem), item, authHeaders(accessToken));
  return res.data;
};

export const deleteItem = async (rowId, accessToken) => {
  const res = await axios.post(apiUrl(routes.reportGroups.DeleteReportGroupItem), JSON.stringify(rowId), authHeaders(accessToken));
  return res.data;
};

/*
 * GetAvailableReports has no wrapper here on purpose: the Add-report list reads it through a
 * devextreme-aspnet-data store so DevExtreme's paging/sort/search params reach the API verbatim.
 * See ReportGroups.js -> availableStore.
 */

/** Restore a group to its default by deleting its override, keyed by its stable GroupKey. */
export const resetGroup = async (groupKey, accessToken) => {
  const res = await axios.post(apiUrl(routes.reportGroups.ResetGroup), JSON.stringify(groupKey), authHeaders(accessToken));
  return res.data;
};

/**
 * Restore a report to its default by deleting its override. Keyed by module + report name + the
 * group it sits in: the same report can be placed in several groups of one module and each
 * placement keeps its own override.
 */
export const resetItem = async (module, name, groupKey, accessToken) => {
  const res = await axios.post(
    `${apiUrl(routes.reportGroups.ResetItem)}?module=${encodeURIComponent(module ?? "")}&name=${encodeURIComponent(
      name ?? ""
    )}&groupKey=${encodeURIComponent(groupKey ?? EMPTY_GUID)}`,
    null,
    authHeaders(accessToken)
  );
  return res.data;
};

/**
 * Matching key derived from an existing (code-defined) sidebar leaf's path.
 * "/om/reports/OMR0003" -> "OMR0003"; "/at/artreport/My Report" -> "My Report".
 */
export const leafKeyFromPath = (path) => {
  if (!path) return null;
  if (path.includes("/artreport/")) {
    return decodeURIComponent((path.split("/artreport/")[1] || "").split("?")[0]);
  }
  const seg = path.split("?")[0].split("/").filter(Boolean).pop();
  return seg ? seg.toUpperCase() : null;
};

/** Matching key for an overlay item coming from the API. */
export const overlayItemKey = (item) => {
  if (!item) return null;
  if (item.SourceTableName === "ABLAYOUT") return item.ReportKey; // exact (layout name)
  return (item.FormName || item.ReportKey || "").toUpperCase();
};

/**
 * Build a leaf node for an overlay item that has no matching code-defined leaf
 * (e.g. an ART report, or a report not in the default menu). Both kinds load through
 * the generic /reports/run route by their Name: ART reports (ABLAYOUT) are flagged
 * with art=1 and open via ABRReport; compiled reports open via Report.js.
 */
export const synthLeafFromOverlay = (item, id) => {
  const label = item.Description || item.Name || item.FormName || item.ReportKey || "";
  const name = item.Name || item.FormName || item.ReportKey || "";
  const moduleLower = (item.Module || "").toLowerCase();
  // Compiled reports get a small, clean /{module}/reports/{name} URL (handled by the
  // per-report routes, which read title/assembly/permission from the DB). ART (ABLAYOUT)
  // reports have no such route and load through the generic /reports/run by format name.
  // Permission is never carried on the URL; routes authorize from the DB by report name.
  const prefix = name.slice(0, 2);
  const path =
    item.SourceTableName === "ABLAYOUT"
      ? `/reports/run?art=1&name=${encodeURIComponent(name)}&title=${encodeURIComponent(label)}`
      : `/${prefix.toLowerCase()}/reports/${name}`;
  return { id, text: label, expanded: false, path, permission: item.Permission || null };
};
